import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import FuzzyProjectSelect from './FuzzyProjectSelect';

const API_URL = process.env.REACT_APP_API_URL || '';

// 获取token
const getToken = () => localStorage.getItem('notes_token') || '';

// fetch with auth
const fetchWithAuth = async (url, options = {}) => {
  const token = getToken();
  return fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      'x-token': token
    }
  });
};

export default function MonitorDataEntry() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectId || '');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [timePoint, setTimePoint] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 加载项目列表
  useEffect(() => {
    loadProjects();
  }, []);

  // 加载配置
  useEffect(() => {
    if (selectedProject) {
      loadConfig(selectedProject);
    } else {
      setLoading(false);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/projects`);
      const data = await res.json();
      if (data.success) {
        setProjects(data.projects || []);
      }
    } catch (e) {
      console.error('加载项目失败:', e);
    }
  };

  const loadConfig = async (pid) => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/config/${pid}`);
      const data = await res.json();
      if (data.success && data.config) {
        setConfig(data.config);
        // 初始化记录
        const initRecords = data.config.scenarios.map(s => ({
          scenarioId: s.id,
          scenarioName: s.name,
          online: '',
          responseTime: ''
        }));
        setRecords(initRecords);
      }
    } catch (e) {
      console.error('加载配置失败:', e);
    }
    setLoading(false);
  };

  const handleProjectChange = (pid) => {
    setSelectedProject(pid);
    if (pid) {
      loadConfig(pid);
    }
  };

  const updateRecord = (idx, field, value) => {
    const newRecords = [...records];
    newRecords[idx] = { ...newRecords[idx], [field]: value };
    setRecords(newRecords);
  };

  const handleSave = async () => {
    if (!selectedProject) {
      alert('请选择项目');
      return;
    }
    if (!selectedProject) {
      alert('请先选择项目');
      return;
    }
    if (!date || !timePoint) {
      alert('请选择日期和时点');
      return;
    }
    if (records.some(r => r.online === '' || r.responseTime === '')) {
      alert('请填写所有场景的数据');
      return;
    }

    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/monitor/data/${selectedProject}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          timePoint,
          records: records.map(r => ({
            scenarioId: r.scenarioId,
            online: parseInt(r.online),
            responseTime: parseFloat(r.responseTime)
          }))
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('数据已保存');
        // 清空表单
        setRecords(records.map(r => ({ ...r, online: '', responseTime: '' })));
      } else {
        alert(data.error || '保存失败');
      }
    } catch (e) {
      alert('保存失败: ' + e.message);
    }
    setSaving(false);
  };

  const copyToTomorrow = () => {
    const tomorrow = new Date(date);
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDate(format(tomorrow, 'yyyy-MM-dd'));
    alert('日期已设置为明天，请修改时点后保存');
  };

  const clearForm = () => {
    setRecords(records.map(r => ({ ...r, online: '', responseTime: '' })));
  };

  if (loading) return <div className="loading">加载中...</div>;

  return (
    <div className="monitor-data-entry">
      <div className="page-header">
        <button onClick={() => navigate('/monitor')} className="btn-back">← 返回</button>
        <h1>📝 监控数据录入</h1>
      </div>

      <div className="entry-form">
        {/* 项目选择 */}
        <div className="form-section">
          <div className="form-group">
            <label>项目：</label>
            <FuzzyProjectSelect
              projects={projects}
              value={selectedProject}
              onChange={handleProjectChange}
              placeholder="请选择项目"
            />
          </div>
        </div>

        {config && (
          <>
            {/* 日期和时点选择 */}
            <div className="form-section">
              <div className="form-row">
                <div className="form-group">
                  <label>日期：</label>
                  <input
                    type="date"
                    value={date}
                    onChange={e => setDate(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>时点：</label>
                  <select
                    value={timePoint}
                    onChange={e => setTimePoint(e.target.value)}
                  >
                    <option value="">请选择时点</option>
                    {config.timePoints.map(tp => (
                      <option key={tp} value={tp}>{tp}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* 场景数据录入 */}
            <div className="form-section">
              <h3>场景数据</h3>
              <div className="records-table">
                <div className="records-header">
                  <div className="col-scenario">场景名称</div>
                  <div className="col-online">在线人数</div>
                  <div className="col-response">响应速度(s)</div>
                </div>
                {records.map((record, idx) => (
                  <div key={idx} className="records-row">
                    <div className="col-scenario">{record.scenarioName}</div>
                    <div className="col-online">
                      <input
                        type="number"
                        value={record.online}
                        onChange={e => updateRecord(idx, 'online', e.target.value)}
                        placeholder="人数"
                      />
                    </div>
                    <div className="col-response">
                      <input
                        type="number"
                        step="0.1"
                        value={record.responseTime}
                        onChange={e => updateRecord(idx, 'responseTime', e.target.value)}
                        placeholder="秒"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="form-actions">
              <button onClick={handleSave} className="btn-primary" disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </button>
              <button onClick={copyToTomorrow} className="btn-secondary">
                复制到明天
              </button>
              <button onClick={clearForm} className="btn-secondary">
                清空
              </button>
              <button onClick={() => navigate('/monitor')} className="btn-secondary">
                取消
              </button>
            </div>
          </>
        )}

        {!config && selectedProject && (
          <div className="empty-hint">
            该项目尚未配置监控，请先
            <button onClick={() => navigate(`/monitor/config/${selectedProject}`)} className="link-btn">
              配置监控
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
